import { WorkDataSuggestionEvaluationRecord, WorkSuggestionPageStore } from './work-suggestion-page.store';
import { Component, computed, effect, inject, input, output, signal, ViewChild, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, Validators } from '@angular/forms';
import { BreadcrumbService } from 'xng-breadcrumb';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { CreateWorkInput, UpdateWorkInput, WorkFileObject, WorkGridItemObject, WorkOfferObject } from '@it2go/types';
import { WorkOfferUpsertInput } from '@libs/types/src/services/input-type/work/work-offer.upsert-input';
import { isFolderReadOnly } from '../../../../../../../../types/src/services/enum/folder-status.enum';
import {
    getEvaluationDetailFrag,
    getEvaluationsFrag,
    getFolderByPersonFrag,
    getFolderFrag,
    getSequenceNumberFrag,
    getWorkDetailFrag,
    getWorkSplitCopyFrag,
} from './work-suggestion-page.query';
import { NgClass } from '@angular/common';
import {
    WorkFormContainerComponent,
} from '../../../../common-service/work/work-detail/work-form-container/work-form-container.component';
import {
    WorkDetailSelectServiceComponent,
} from '../../../../medical-service/work/work-detail/work-detail-select-service/work-detail-select-service.component';
import {
    SelectPersonComponent,
} from '../../../../../modules/content/components/common/select-person/select-person.component';
import { DateInputComponent } from '../../../../kit/input/date-input/date-input.component';
import { NumberInputComponent } from '../../../../kit/input/text-input/number-input.component';
import {
    InputWysiwygComponent,
} from '../../../../../modules/shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { SubscriptionComponent } from '../../../../../modules/shared/helpers/subscription.component';
import { BaseDialogService } from '../../../../kit/base-dialog/base-dialog-service';
import { WorkDataSuggestionObject } from '@libs/types/src/services/object-type/work/work-data-suggestion.object';
import { WorkDataSuggestionTypeEnum } from '@libs/types/src/services/enum/work-data-suggestion-type.enum';
import { AppFormGroup, changeSignal, disableFormControls, formSetText } from '../../../../../utils/form.utils';
import { fb, goBack, routeParams, translate } from '../../../../../modules/shared/shared.global-service';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { isInvalid } from '../../../../../modules/shared/helpers/validate';
import { dateify, dateifyOrNow } from '../../../../../modules/shared/helpers/date.helper';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { ServiceTypeEnum } from '../../../../../../../../types/src/services/enum/service-type.enum';
import { SwitchInputComponent } from '../../../../kit/input/switch-input/switch-input.component';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import { CheckInputComponent } from '../../../../kit/input/check-input/check-input.component';
import { priorityEnumOptions } from '@libs/types/src/common/enum/priority.enum';
import {
    WorkDataSuggestionEvaluationStateEnum,
    workDataSuggestionEvaluationStateEnumOptions,
} from '@libs/types/src/services/enum/work-data-suggestion-evaluation-state.enum';
import {
    SelectUserComponent,
} from '../../../../../modules/content/components/common/select-user/select-user.component';
import { ListboxChangeEvent, ListboxModule } from '@openng/optimus-ui/listbox';
import { TranslatePipe } from '@ngx-translate/core';
import { UserChipComponent } from '../../../../kit/user-chip/user-chip.component';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { InfiniteScrollStore } from '../../../../../modules/shared/store/common/infinite-scroll.store';
import { FileUploadBoxComponent } from '../../../../kit/file-upload-box/file-upload-box.component';
import { DefaultCategoryTypeEnum } from '@libs/types/src/file-manager/types/default-category.type';
import { FileKindEnum } from '@libs/types/src/file-manager/enum/file-kind.enum';
import { ExternalType } from '@libs/types/src/file-manager/enum/external-type.enum';
import {
    SocialServiceSelectOfferComponent,
} from '../../social-service-select-offer/social-service-select-offer.component';
import { IconComponent } from '../../../../kit/icon/icon.component';
import { ServicePatternStructureUsageEnum } from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import {
    ServicePatternWysiwygComponent,
} from '../../../../../modules/content/components/common/service-pattern-wysiwyg/service-pattern-wysiwyg.component';
import { SocialServiceOperationEnum } from '@libs/types/src/services/enum/social-service-operation.enum';
import { ContextMenu } from '@openng/optimus-ui/contextmenu';
import { MenuItem } from '@openng/optimus-ui/api';
import { suggestionFolderUri } from '../../../folder/suggestions/folder-suggestions-page.component';
import {
    WorkDataSuggestionEvaluationObject,
} from '@libs/types/src/services/object-type/work/work-data-suggestion-evaluation.object';
import { CardComponent } from '../../../../kit/card/card.component';

@Component({
    selector: 'app-work-suggestion-page',
    templateUrl: './work-suggestion-page.component.html',
    styleUrl: './work-suggestion-page.component.scss',
    providers: [WorkSuggestionPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgClass,
        WorkFormContainerComponent,
        WorkDetailSelectServiceComponent,
        SelectPersonComponent,
        DateInputComponent,
        NumberInputComponent,
        InputWysiwygComponent,
        SwitchInputComponent,
        SelectInputComponent,
        CheckInputComponent,
        SelectUserComponent,
        ListboxModule,
        FormsModule,
        TranslatePipe,
        UserChipComponent,
        FileUploadBoxComponent,
        SocialServiceSelectOfferComponent,
        IconComponent,
        ServicePatternWysiwygComponent,
        ContextMenu,
        CardComponent,
    ],
})
export class WorkSuggestionPageComponent extends SubscriptionComponent {

    public readonly store = inject(WorkSuggestionPageStore);
    public readonly breadcrumbService = inject(BreadcrumbService);
    public readonly router = inject(Router);
    public readonly route = inject(ActivatedRoute);
    public readonly dialogService = inject(BaseDialogService);

    public readonly selectPersonComponent = viewChild(SelectPersonComponent);
    protected readonly filesUpload = viewChild(FileUploadBoxComponent);

    public readonly suggestionTypeInput = input<WorkDataSuggestionTypeEnum | null>(null);
    public readonly personIdInput = input<number | null>(null);
    closeCallback = output<void>();

    protected selectEvaluation: WorkDataSuggestionEvaluationRecord | null = null;
    protected preselectedOffers = signal<WorkOfferObject[]>([]);

    protected prefix = 'workData.suggestion.';
    protected existingWork: WorkGridItemObject<WorkDataSuggestionObject> | null = null;
    protected worksSplit = signal<any>(null);
    protected queryParams = toSignal(this.route.queryParams, { initialValue: this.route.snapshot.queryParams });
    protected useSplitter = computed(() => !!this.queryParams()['split']);

    protected form = new AppFormGroup({
        dateFrom: fb.control<Date>(new Date(), Validators.required),
        type: fb.control<string | null>(null, Validators.required),
        sequenceNumber: fb.control<number>(1),
        description: fb.control<string>(''),
        note: fb.control<string | null>(''),
        complaintTypeId: fb.control<number | null>(null),
        priority: fb.control<string | null>(null),
        userId: fb.control<string | null>(null, Validators.required),
        files: fb.control<WorkFileObject[]>([]),
        serviceId: fb.control<number | null>(null, Validators.required),
        personId: fb.control<number | null>(null, Validators.required),
        // versioned
        state: fb.control<string>(WorkDataSuggestionEvaluationStateEnum.inProgress, Validators.required),
        onlyForWorker: fb.control<boolean>(false),
        evaluation: fb.control<string | null>(''),
        evaluationDate: fb.control<Date | null>(new Date(), Validators.required),
        evaluatorId: fb.control<string | null>(null),
        offerId: fb.control<number>(0, Validators.required),
        applications: fb.control<number>(0, Validators.required),
        clientPresence: fb.control<boolean>(false),
    });

    protected serviceId = changeSignal(this.form.controls.serviceId);
    protected personId = changeSignal(this.form.controls.personId);
    protected suggestionType = changeSignal(this.form.controls.type);
    protected patternUsage = computed(() => {
        switch (this.suggestionType()) {
            case WorkDataSuggestionTypeEnum.satisfaction:
                return ServicePatternStructureUsageEnum.SUGGESTION_SATISFACTION;
            case WorkDataSuggestionTypeEnum.complaint:
                return ServicePatternStructureUsageEnum.SUGGESTION_COMPLAINT;
            case WorkDataSuggestionTypeEnum.finding:
                return ServicePatternStructureUsageEnum.SUGGESTION_FINDING;
            default:
                return null;
        }
    });

    protected complaintTypesScroll = computed(() => {
        const serviceId = this.serviceId();
        if (!serviceId) return null;

        return new InfiniteScrollStore(
            this.store.api.getComplaintTypes,
            new FilterBuilder()
                .where('serviceId', serviceId)
                .where('active', '1')
                .singlePage(),
            true,
            'id name daysToProcess',
            undefined,
            true,
        );
    });
    protected terminated = signal(false);
    protected dateFrom = changeSignal(this.form.controls.dateFrom);
    protected lastDateFrom = signal(this.dateFrom());
    protected folderId = computed(() => {
        return this.store.data.getFolderByPerson()?.id || 0;
    });

    @ViewChild('contextMenu') ctx!: ContextMenu;
    protected contextMenuItemsAll: MenuItem[] = [
        {
            label: translate.instant('common.update'),
            icon: 'pi pi-file-edit', command: () => this.updateEvaluation(),
        },
        {
            label: translate.instant('common.delete'),
            icon: 'pi pi-times', command: () => this.deleteEvaluation(),
        },
    ];
    protected contextMenuItems = this.contextMenuItemsAll;
    private contextItemId: number = 0;

    private personIdFromQuery = 0;
    private lastEvaluation: WorkDataSuggestionEvaluationObject | null = null;

    constructor() {
        super();
        const routeSuggestionType: WorkDataSuggestionTypeEnum | null = this.route.snapshot.data['suggestionType'] ?? null;
        const type = routeSuggestionType ?? this.suggestionTypeInput();
        if (type) {
            this.form.patchValue({ type }, { isCodeUpdate: true });
        }

        this.personIdFromQuery = this.route.snapshot.queryParams['personId'];
        if (this.personIdFromQuery) {
            this.form.patchValue({ personId: Number(this.personIdFromQuery) }, { isCodeUpdate: true });
        }

        this.store.onSuccess(
            () => this.loadEvaluations(Number(routeParams.workId())),
            'updateWorkSuggestionEvaluation',
            'deleteWorkSuggestionEvaluation',
        );

        effect(() => {
            const type = this.suggestionTypeInput();
            if (type) {
                this.form.patchValue({ type }, { isCodeUpdate: true });
            }
        });
        effect(() => {
            const personId = this.personIdInput();
            if (personId && !this.personId()) {
                this.form.patchValue({ personId }, { isCodeUpdate: true });
            }
        });

        effect(async () => { // Load last sequenceNumber
            if (routeParams.workId() != '0' || !this.serviceId() || !this.suggestionType()) return;

            const res = await this.store.api.getWorks(FilterBuilder.singleItem({
                serviceId: this.serviceId() || 0,
                workType: WorkTypeEnum.SUGGESTION,
                'workData.swdsg.type': this.suggestionType(),
                yearFrom: { EQ: new Date().toISOString() },
                sort: { column: 'workData.swdsg.sequenceNumber', direction: 'DESC' },
            }), getSequenceNumberFrag);

            const item: WorkGridItemObject<WorkDataSuggestionObject> = res?.items?.[0];
            this.form.patchValue({ sequenceNumber: (item?.workData?.sequenceNumber || 0) + 1 });
        });

        effect(() => {
            const workId = Number(routeParams.workId());
            if (workId) {
                this.breadcrumbService.set('@id', `id|${workId}`);
                this.store.api.getWorks(FilterBuilder.filter({ id: workId }), getWorkDetailFrag)
                    .then((work) => {
                        this.existingWork = work?.['items']?.[0];
                        this.patchFormWithExistingWork(true);
                    });
                this.loadEvaluations(workId);
            }
        });

        effect(() => {
            const evaluationRecords = this.store.evaluationRecords();
            if (evaluationRecords.length > 0) {
                this.selectEvaluation = evaluationRecords[0];
                this.loadAndPatchEvaluation(this.selectEvaluation.id);
            }
        });

        effect(() => {
            const selectPerson = this.selectPersonComponent();
            const serviceId = Number(this.serviceId()) || null;
            const personId = Number(this.personId()) || null;
            if (
                serviceId
                && personId
                && selectPerson?.serviceId() === serviceId
                && selectPerson.personScroll().loading() === false
            ) {
                const exists = selectPerson.personScroll().items().find((it) => it.id === personId);
                if (!exists) {
                    const isExistingSelection = this.existingWork?.serviceId === serviceId
                        && this.existingWork.person?.id === personId;
                    if (!isExistingSelection) {
                        this.form.patchValue({ personId: null });
                    }
                } else {
                    this.store.api.getFolderByPerson({
                        serviceId,
                        personId,
                    }, getFolderByPersonFrag).then();
                }
            }
        });

        effect(async () => {
            const userId = this.store.globalGlobalStore.loggedUserStore.currentUserId();
            if (!userId) return;

            this.form.patchValue({ userId, evaluatorId: userId });
        });

        effect(() => {
            this.folderId() && this.store.api.getFolder({ id: this.folderId() }, getFolderFrag).then();
        });

        effect(() => {
            this.terminated.set(isFolderReadOnly(this.store.data.getFolder()) || !this.store.isAllowed('workSuggestion.create')());
            disableFormControls(this.form, this.terminated(), ['serviceId', 'personId']);
        });

        effect(() => {
            const date = this.store.workDate();
            const newDate = new Date();
            date.setHours(newDate.getHours());
            date.setMinutes(newDate.getMinutes());
            this.form.patchValue({ dateFrom: date });
        });

        effect(() => {
            const works = this.worksSplit();
            const params = this.queryParams();
            const workId = params['split'] ?? params['copy'] ?? null;
            const loadedWorks = works?.['items'] || [];
            const loadedWork = loadedWorks.length ? loadedWorks[0] : null;
            if (workId && loadedWork?.id == workId) {
                this.existingWork = loadedWork;
                if (
                    !this.useSplitter() &&
                    params['copy']
                ) {
                    this.patchFormWithExistingWork();
                }
            }
        });

        effect(async () => {
            const params = this.queryParams();
            const workId = params['split'] ?? params['copy'] ?? null;

            if (!workId) {
                this.worksSplit.set(null);
                return;
            }

            this.worksSplit.set(
                await this.store.api.getWorks(FilterBuilder.singlePage({ id: workId }), getWorkSplitCopyFrag),
            );
        });
    }

    public async submit(): Promise<void> {
        if (isInvalid(this.form)) return;
        if (!this.store.folder()) return;
        const fileIds = await this.uploadFiles();

        const _data = {
            dateFrom: dateifyOrNow(this.form.value.dateFrom).toISOString(),
            userIds: [this.form.value.userId],
            fileIds,
            workData: JSON.stringify({
                type: this.form.value.type || '',
                sequenceNumber: this.form.value.sequenceNumber || 1,
                description: this.form.value.description || '',
                note: this.form.value.note || '',
                complaintTypeId: this.form.value.complaintTypeId || null,
                priority: this.form.value.priority || null,
                additionalData: this.newEvaluation(),
            }),
        };

        const workId = Number(routeParams.workId() || 0);
        if (workId) {
            const data = _data as UpdateWorkInput;
            data.id = workId;
            await this.store.api.updateWork(data);
            contentGlobalSuccess.next('common.updated');
        } else {
            const data = _data as CreateWorkInput;
            data.folderId = this.store.folder()!.id;
            data.workType = WorkTypeEnum.SUGGESTION;
            await this.store.api.createWork(data);
            contentGlobalSuccess.next('common.created');
        }
        await this.confirmFiles(fileIds);

        this.close();
    }

    public close() {
        // opened as modal
        if (this.suggestionTypeInput()) {
            this.closeCallback.emit();
            // opened from folder
        } else if (this.personIdFromQuery) {
            this.router.navigateByUrl(suggestionFolderUri(this.form.controls.type.value!, this.folderId()!));
        } else {
            goBack();
        }
    }

    public async updateEvaluation() {
        if (this.contextItemId) {
            await this.store.api.updateWorkSuggestionEvaluation({
                id: this.contextItemId,
                state: this.form.value.state || '',
                onlyForWorker: this.form.value.onlyForWorker || false,
                evaluation: this.form.value.evaluation || '',
                evaluationDate: this.form.value.evaluationDate?.toISOString() || null,
                evaluatorId: this.form.value.evaluatorId || null,
                offerId: this.form.value.offerId || 0,
                applications: this.form.value.applications || 0,
                clientPresence: this.form.value.clientPresence || false,
            });
            contentGlobalSuccess.next('common.updated');
        }
    }

    public async deleteEvaluation() {
        if (this.contextItemId) {
            await this.store.api.deleteWorkSuggestionEvaluation({ id: this.contextItemId });
            contentGlobalSuccess.next('common.deleted');
        }
    }

    protected async loadEvaluations(workId: number): Promise<void> {
        if (!workId) return;
        const filter = new FilterBuilder()
            .where('workId', String(workId))
            .sort('evaluationDate', 'DESC').create();
        const result = await this.store.api.getWorkSuggestionEvaluations(
            filter,
            getEvaluationsFrag,
        );
        this.store.evaluations.set(result?.['items'] || []);
    }

    protected evaluationListChange(event: ListboxChangeEvent): void {
        if (event.value) {
            this.selectEvaluation = this.store.evaluationRecords()[event.value.index];
            this.loadAndPatchEvaluation(this.selectEvaluation.id);
        }
    }

    protected rightClick(menu: ContextMenu, event: MouseEvent, item: any): void {
        this.contextItemId = item.id;
        menu.hide();
        event.preventDefault();
        this.contextMenuItems = this.contextMenuItemsAll.slice(this.selectEvaluation?.id === item.id ? 0 : 1);
        setTimeout(() => {
            menu.toggle(event);
        }, 1);
    }

    private patchFormWithExistingWork(edit: boolean = false): void {
        if (!this.existingWork) return;

        this.form.patchValue({
            serviceId: this.existingWork.serviceId,
            personId: this.existingWork.person?.id,
            dateFrom: dateifyOrNow(this.existingWork.dateFrom),
            userId: this.existingWork.users?.[0]?.id,
            files: this.existingWork?.files || [],
            type: this.existingWork.workData?.type || '',
            description: this.existingWork.workData?.description || '',
            note: this.existingWork.workData?.note || '',
            complaintTypeId: this.existingWork.workData?.complaintTypeId || null,
            priority: this.existingWork.workData?.priority || null,
        }, { isCodeUpdate: true });
        if (edit) {
            this.form.patchValue({
                dateFrom: dateifyOrNow(this.existingWork.dateFrom),
                sequenceNumber: this.existingWork.workData?.sequenceNumber || 1,
            }, { isCodeUpdate: true });
            this.lastDateFrom.set(dateifyOrNow(this.existingWork.dateFrom));
        }
        if (this.existingWork.workData?.lastEvaluation) {
            this.patchEvaluation(this.existingWork.workData.lastEvaluation);
        }
    }

    private async loadAndPatchEvaluation(id: number): Promise<void> {
        const result = await this.store.api.getWorkSuggestionEvaluations(
            { filter: [[{ column: 'id', operator: 'EQ', value: [String(id)] }]] },
            getEvaluationDetailFrag,
        );
        const evaluation = result?.['items']?.[0];
        this.patchEvaluation(evaluation);
    }

    private patchEvaluation(evaluation: WorkDataSuggestionEvaluationObject) {
        if (!evaluation) return;
        this.lastEvaluation = evaluation;
        this.form.patchValue({
            state: evaluation.state,
            onlyForWorker: evaluation.onlyForWorker,
            evaluation: evaluation.evaluation || '',
            evaluationDate: dateify(evaluation.evaluationDate) || null,
            evaluatorId: evaluation.evaluatorId || null,
            offerId: evaluation.offerId || 0,
            applications: evaluation.applications || 0,
            clientPresence: evaluation.clientPresence,
        }, { isCodeUpdate: true });
        this.preselectedOffers.set(
            evaluation.offerId
                ? [{
                    offerId: evaluation.offerId,
                    applications: evaluation.applications || 0,
                    operations: [], procedures: [],
                } as unknown as WorkOfferObject]
                : [],
        );
    }

    private async uploadFiles(): Promise<number[]> {
        const metadata = {
            categoryCode: DefaultCategoryTypeEnum.SUGGESTIONS,
            note: null,
            date: new Date(),
            kind: FileKindEnum.received,
            accesses: [
                {
                    externalType: ExternalType.folder,
                    externalId: String(this.store.folder()!.id),
                },
                {
                    externalType: ExternalType.service,
                    externalId: String(
                        this.store.globalGlobalStore.currentService()?.id,
                    ),
                },
            ],
        };

        return this.filesUpload()?.uploadFiles(metadata) || [];
    }

    private async confirmFiles(fileIds: number[]) {
        return this.filesUpload()?.confirmFiles(fileIds);
    }

    protected onOfferSelected(offers: WorkOfferUpsertInput[]): void {
        this.form.patchValue({
            offerId: offers[0]?.offerId || 0,
            applications: offers[0]?.applications || 0,
        });
    }

    private newEvaluation() {
        if (
            (this.form.controls.state.value === this.lastEvaluation?.state)
            && (this.form.controls.onlyForWorker.value === this.lastEvaluation?.onlyForWorker)
            && (this.form.controls.evaluation.value === this.lastEvaluation?.evaluation)
            && (this.form.controls.evaluationDate.value?.toISOString() === this.lastEvaluation?.evaluationDate)
            && (this.form.controls.evaluatorId.value === this.lastEvaluation?.evaluatorId)
            && (this.form.controls.offerId.value === this.lastEvaluation?.offerId)
            && (this.form.controls.applications.value === this.lastEvaluation?.applications)
            && (this.form.controls.clientPresence.value === this.lastEvaluation?.clientPresence)
        ) {
            return null;
        }

        let evaluationDate = this.form.value.evaluationDate?.toISOString();
        if (this.form.value.evaluationDate?.toISOString() == this.selectEvaluation?.evaluationDate) {
            evaluationDate = new Date().toISOString();
        }

        return {
            state: this.form.value.state || '',
            onlyForWorker: this.form.value.onlyForWorker || false,
            evaluation: this.form.value.evaluation || '',
            evaluationDate: evaluationDate || null,
            evaluatorId: this.form.value.evaluatorId,
            offerId: this.form.value.offerId || 0,
            applications: this.form.value.applications || 0,
            clientPresence: this.form.value.clientPresence || false,
        };
    }

    protected readonly priorityEnumOptions = priorityEnumOptions;
    protected readonly workDataSuggestionEvaluationStateEnumOptions = workDataSuggestionEvaluationStateEnumOptions;
    protected readonly WorkDataSuggestionTypeEnum = WorkDataSuggestionTypeEnum;
    protected readonly formSetText = formSetText;
    protected readonly WorkTypeEnum = WorkTypeEnum;
    protected readonly ServiceTypeEnum = ServiceTypeEnum;
    protected readonly SocialServiceOperationEnum = SocialServiceOperationEnum;
}
